import React, { useState, useCallback } from 'react'
import LoginForm from './LoginForm'
import RegistrationForm from './RegistrationForm'
import EnterCodeForm from './EnterCodeForm'
import RecoveryForm from './RecoveryForm'
import { TOneItemCallback } from 'HttpRequests'
import MySpinner from 'components/MySpinner'

type TForm = 'login' | 'register' | 'enter_code' | 'recovery'

export interface ILoginFormProps {
  onLogin: (
    login: string,
    password: string,
    isRemember: boolean,
    captcha: string,
    captchaHash: string,
    callback: (success: boolean, status?: string, tries?: number) => void
  ) => void
  onRegister: (
    name: string,
    login: string,
    email: string,
    password: string,
    secondPassword: string,
    captcha: string,
    captchaHash: string,
    callback: (success: boolean, status?: string, userId?: number) => void
  ) => void
  onEnterCode: (
    userId: number,
    code: number,
    isRemember: boolean,
    callback: TOneItemCallback<boolean>
  ) => void
  onClose: VoidFunction
}

interface IOnlyLoginFormProps {
  onIsRegistration: TOneItemCallback<boolean>
}

const VolatileLoginForm: React.FC<ILoginFormProps & IOnlyLoginFormProps> = ({
  onLogin,
  onRegister,
  onEnterCode,
  onClose,
  onIsRegistration,
}) => {
  const [loginPhone, setLoginPhone] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [secondPassword, setSecondPassword] = useState<string>('')
  const [userIdAfterReg, setUserIdAfterReg] = useState<number | undefined>(undefined)
  const [isRememberAfterReg, setIsRememberAfterReg] = useState(false)
  const [form, setForm] = useState<TForm>('login')

  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [showCaptcha, setShowCaptcha] = useState(false)

  // const [isLogining, setIsLogining] = useState(true)
  // const [isEnterCode, setIsEnterCode] = useState(false)
  // const [isRecovery, setIsRecovery] = useState(false)

  const toLogining = useCallback(() => {
    setForm('login')
    setMessage('')
    onIsRegistration(false)
  }, [onIsRegistration])

  const toRegistration = useCallback(() => {
    setForm('register')
    setMessage('')
    onIsRegistration(true)
  }, [onIsRegistration])

  const toRecovery = useCallback(() => {
    setForm('recovery')
    setMessage('')
  }, [])

  const handleLogin = useCallback(
    (login, password, isRemember, captcha, captchaHash,) => {
      setIsLoading(true)
      setMessage('')
      onLogin(login, password, isRemember, captcha, captchaHash, (success, status, tries) => {
        setIsLoading(false)
        if (!success) {
          if (status === 'NOT_ACTIVE') {
            setMessage('Вход с данного номера телефона недоступен')
          } else if (status === 'CAPTCHA_ERROR') {
              setMessage('Попробуйте ещё раз')
          } else {
            setMessage('Неверный логин или пароль')
          }
          if (tries && tries > 2) {
            setShowCaptcha(true)
          }
        } else {
          onClose()
        }
      })
    },
    [onClose, onLogin]
  )

  const handleRegister = useCallback(
    (name, login, email, firstPassword, secondPassword, isRemember, captcha, captchaHash) => {
      setIsLoading(true)
      setMessage('')
      setIsRememberAfterReg(isRemember)
      onRegister(
        name,
        login,
        email,
        firstPassword,
        secondPassword,
        captcha,
        captchaHash,
        (success, status, userId) => {
          setIsLoading(false)
          if (success && status === 'OK') {
            setUserIdAfterReg(userId)
            setForm('enter_code')
          } else if (status === 'ALREADY_REGISTERED') {
            setMessage('Пользователь уже зарегистрирован')
          } else if (status === 'INVALID_PHONE_NUMBER') {
            setMessage('Неверно набран номер')
          } else if (status === 'INVALID_PASSWORD') {
            setMessage('Введите другой пароль')
          } else if (status === 'NOT_SAME_PASSWORDS') {
            setMessage('Пароли должны совпадать')
          } else if (status === 'CANT_SEND_CODE') {
            setMessage('Невозможно отправить СМС-код подтверждения')
          } else if (status === 'SERVICE_NOT_AVAILABLE') {
            setMessage('Сервис временно недоступен')
          } else if (status === 'NOT_ACTIVE') {
            setMessage('Регистрация для данного номера телефона недоступна')
          } else if (status === 'CAPTCHA_ERROR') {
            setMessage('Попробуйте ещё раз')
          } else {
            setMessage('Неизвестная ошибка при регистрации')
          }
        }
      )
    },
    [onRegister]
  )

  const handleEnterCode = useCallback(
    (code) => {
      if (userIdAfterReg) {
        onEnterCode(userIdAfterReg, code, isRememberAfterReg, (success) => {
          if (!success) {
            setMessage('Неверный код или его срок действия истек.')
          } else {
            onClose()
          }
        })
      } else {
        setForm('register')
        setMessage('Произошла ошибка, попробуйте еще раз')
      }
    },
    [userIdAfterReg, isRememberAfterReg, onEnterCode, onClose]
  )

  return (
    <div>
      {form === 'login' ? (
        <LoginForm
          loginPhone={loginPhone}
          isLoading={isLoading}
          onChangeLoginPhone={setLoginPhone}
          password={password}
          onChangePassword={setPassword}
          onLogin={handleLogin}
          onGoToRegister={toRegistration}
          onGoToRecovery={toRecovery}
          onSetMessage={setMessage}
          showCaptcha={showCaptcha}
        />
      ) : form === 'register' ? (
        <RegistrationForm
          isLoading={isLoading}
          loginPhone={loginPhone}
          onChangeLoginPhone={setLoginPhone}
          firstPassword={password}
          secondPassword={secondPassword}
          onChangeFirstPassword={setPassword}
          onChangeSecondPassword={setSecondPassword}
          onRegister={handleRegister}
          onGoToLogin={toLogining}
          onSetMessage={setMessage}
        />
      ) : userIdAfterReg && form === 'enter_code' ? (
        <EnterCodeForm
          onGoToRegistration={toRegistration}
          onEnterCode={handleEnterCode}
          onSetMessage={setMessage}
        />
      ) : form === 'recovery' ? (
        <RecoveryForm
          loginPhone={loginPhone}
          onChangeLoginPhone={setLoginPhone}
          onCancel={toLogining}
          onLogin={handleLogin}
          onSetMessage={setMessage}
        />
      ) : (
        <MySpinner />
      )}

      {message && <div style={{ color: 'var(--color-red)' }}>{message}</div>}
    </div>
  )
}
export default VolatileLoginForm
